import * as THREE from 'three';

function prismGeometry(points, thickness) {
  const vertices = [];
  const indices = [];
  const half = thickness / 2;
  for (const y of [-half, half]) {
    for (const [x, z] of points) vertices.push(x, y, z);
  }
  const count = points.length;
  for (let index = 1; index < count - 1; index += 1) {
    indices.push(0, index + 1, index);
    indices.push(count, count + index, count + index + 1);
  }
  for (let index = 0; index < count; index += 1) {
    const next = (index + 1) % count;
    indices.push(index, next, count + next, index, count + next, count + index);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function shadowed(mesh) {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export class AircraftView {
  constructor(scene) {
    this.group = new THREE.Group();
    this.group.name = 'Falcon S9 aircraft';
    this.group.rotation.order = 'YXZ';
    this.propellerAngle = 0;
    this.createModel();
    scene.add(this.group);
  }

  createModel() {
    const pearl = new THREE.MeshPhysicalMaterial({
      color: '#e9eceb',
      metalness: 0.64,
      roughness: 0.23,
      clearcoat: 1,
      clearcoatRoughness: 0.12
    });
    const navy = new THREE.MeshPhysicalMaterial({
      color: '#102d42',
      metalness: 0.7,
      roughness: 0.2,
      clearcoat: 0.9
    });
    const gold = new THREE.MeshStandardMaterial({
      color: '#d59b37', metalness: 0.72, roughness: 0.25
    });
    const dark = new THREE.MeshStandardMaterial({
      color: '#15191c', metalness: 0.6, roughness: 0.34
    });
    const glass = new THREE.MeshPhysicalMaterial({
      color: '#163b52',
      metalness: 0.12,
      roughness: 0.06,
      transmission: 0.35,
      transparent: true,
      opacity: 0.88,
      clearcoat: 1
    });

    const profile = [
      new THREE.Vector2(0.08, -5.5),
      new THREE.Vector2(0.75, -4.8),
      new THREE.Vector2(1.08, -2.6),
      new THREE.Vector2(1.16, 0.2),
      new THREE.Vector2(0.86, 3.4),
      new THREE.Vector2(0.35, 5.9),
      new THREE.Vector2(0.04, 6.4)
    ];
    const fuselage = shadowed(new THREE.Mesh(new THREE.LatheGeometry(profile, 28), pearl));
    fuselage.rotation.x = Math.PI / 2;
    this.group.add(fuselage);

    const wingPoints = [
      [-10.8, 0.25], [-1.2, -2.05], [1.2, -2.05], [10.8, 0.25],
      [9.5, 1.55], [1.1, 0.82], [-1.1, 0.82], [-9.5, 1.55]
    ];
    const wings = shadowed(new THREE.Mesh(prismGeometry(wingPoints, 0.3), navy));
    wings.position.set(0, 0.05, -0.2);
    this.group.add(wings);

    const stripePoints = [
      [-10.2, 0.5], [-2, -1.6], [2, -1.6], [10.2, 0.5],
      [9.65, 0.85], [1.5, -0.8], [-1.5, -0.8], [-9.65, 0.85]
    ];
    const wingStripe = new THREE.Mesh(prismGeometry(stripePoints, 0.045), gold);
    wingStripe.position.y = 0.22;
    this.group.add(wingStripe);

    const tail = shadowed(new THREE.Mesh(
      prismGeometry([[-4.1, -0.15], [-0.45, -0.8], [0.45, -0.8], [4.1, -0.15], [3.5, 0.8], [-3.5, 0.8]], 0.2),
      navy
    ));
    tail.position.set(0, 0.1, 4.6);
    this.group.add(tail);

    const finShape = new THREE.Shape();
    finShape.moveTo(0, 0);
    finShape.lineTo(0, 3.2);
    finShape.lineTo(0.42, 3.55);
    finShape.lineTo(1.15, 0);
    finShape.closePath();
    const fin = shadowed(new THREE.Mesh(
      new THREE.ExtrudeGeometry(finShape, { depth: 0.18, bevelEnabled: true, bevelSize: 0.06, bevelThickness: 0.05 }),
      navy
    ));
    fin.rotation.y = Math.PI / 2;
    fin.position.set(-0.09, 0.45, 4.2);
    this.group.add(fin);

    const cockpit = shadowed(new THREE.Mesh(new THREE.SphereGeometry(1, 28, 14), glass));
    cockpit.scale.set(0.83, 0.55, 1.65);
    cockpit.position.set(0, 0.82, -1.45);
    this.group.add(cockpit);

    const nose = shadowed(new THREE.Mesh(new THREE.ConeGeometry(0.72, 1.55, 24), navy));
    nose.rotation.x = -Math.PI / 2;
    nose.position.z = -5.9;
    this.group.add(nose);

    this.propeller = new THREE.Group();
    this.propeller.position.z = -6.72;
    const hub = shadowed(new THREE.Mesh(new THREE.SphereGeometry(0.34, 16, 8), gold));
    const bladeGeometry = new THREE.CapsuleGeometry(0.16, 2.7, 4, 10);
    for (const angle of [0, Math.PI / 2]) {
      const blade = shadowed(new THREE.Mesh(bladeGeometry, dark));
      blade.rotation.z = angle;
      this.propeller.add(blade);
    }
    this.propeller.add(hub);
    this.group.add(this.propeller);

    const gearMaterial = new THREE.MeshStandardMaterial({ color: '#202427', roughness: 0.7 });
    const wheelGeometry = new THREE.TorusGeometry(0.34, 0.14, 8, 18);
    this.gear = new THREE.Group();
    for (const [x, z] of [[-2.35, -0.35], [2.35, -0.35], [0, 4.2]]) {
      const strut = shadowed(new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.08, 1.45, 8), dark));
      strut.position.set(x, -0.78, z);
      strut.rotation.z = x === 0 ? 0 : x > 0 ? -0.22 : 0.22;
      const wheel = shadowed(new THREE.Mesh(wheelGeometry, gearMaterial));
      wheel.position.set(x, -1.52, z);
      wheel.rotation.y = Math.PI / 2;
      this.gear.add(strut, wheel);
    }
    this.group.add(this.gear);

    const red = new THREE.MeshBasicMaterial({ color: '#ff2f36' });
    const green = new THREE.MeshBasicMaterial({ color: '#43ffa0' });
    const lightGeometry = new THREE.SphereGeometry(0.18, 12, 8);
    const leftLight = new THREE.Mesh(lightGeometry, red);
    leftLight.position.set(-10.75, 0.18, 0.35);
    const rightLight = new THREE.Mesh(lightGeometry, green);
    rightLight.position.set(10.75, 0.18, 0.35);
    this.group.add(leftLight, rightLight);

    this.group.scale.setScalar(0.82);
  }

  update(flight, dt) {
    this.group.position.set(flight.position.x, flight.position.y, flight.position.z);
    this.group.rotation.set(flight.pitch, flight.heading, flight.roll);
    this.propellerAngle += dt * (10 + flight.throttle * 62);
    this.propeller.rotation.z = this.propellerAngle;
    const gearDown = flight.grounded || flight.gearDown;
    this.gear.position.y = THREE.MathUtils.lerp(this.gear.position.y, gearDown ? 0 : 0.5, 0.07);
    this.gear.scale.setScalar(THREE.MathUtils.lerp(this.gear.scale.x, gearDown ? 1 : 0.22, 0.08));
  }
}
